import {
  Spinner,
  Heading,
  Button,
  HStack,
  VStack,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Box,
  Stack,
  Center,
  Show,
  Spacer,
  useBreakpointValue,
  Tooltip,
  Text,
} from "@chakra-ui/react";
import { Edit, ImageIcon, PlusIcon, WrenchIcon } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { format } from "../../format";
import { Sponsors } from "../../sponsors/sponsors";

import AdminToolbar from "../Admin/AdminToolbar";
import PostFlow from "../Posts/PostFlow";
import { useEffect, useState } from "react";
import { SetupSignupModal } from "./Modals/SetupSignupModal";
import { useAwaitResponse } from "../../Api/HttpRequestHelpers";
import { CompetitionClient } from "../../Api/client/CompetitionClient";
import { Role, useSessionState } from "../../Session/SessionService";
import { NotFoundImage } from "../notFoundPage";
import { sponsorClient } from "../../Api/BackendClient2";
import { competitionSponsorTypes, SponsorContext } from "../../sponsors/modify_sponsors";
import { ResultDownloadPopupView } from "./Results/ResultDownloadView";

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const CompetitionView = () => {
  const { competitionTag } = useParams() as { competitionTag: string };
  const [competition, isLoadingCompetition, setCompetition] = useAwaitResponse(
    null,
    () => CompetitionClient.getCompetition(competitionTag),
    (response) => response.status == 200 ? response.body ?? null : null
  );


  const session = useSessionState();

  const [showCompetitionDescription, setShowCompetitionDescription] =
    useState<boolean>(false);

  const [openPostCreationModal, setOpenPostCreationModal] =
    useState<boolean>(false);
  const [openPostCreationModal2, setOpenPostCreationModal2] =
    useState<boolean>(false);
  const [disablePostEditing, setDisablePostEditing] = useState<boolean>(true);

  const [configSignup, setConfigSetup] = useState(false);
  const isSmallScreen = useBreakpointValue({ base: false, md: true });

  const [sponsors, isLoadingSponsors, setSponsors] = useAwaitResponse(
    [],
    () => sponsorClient.getSponsors(),
    (response) => response 
  );


  if (isLoadingCompetition) {
    return <Spinner />;
  }

  if (!competition) {
    return (
      <div style={{width: "100%"}}>
        <PageBreadcrumbs competitionName={competitionTag} />
        <Center width={"100%"}>
          <NotFoundImage/>
        </Center>
      </div>
    );
  }
  else if ((competition.deleted) && !(session.hasAtLeastRole(Role.OFFICIAL))) {
    return (
      <div>
        <PageBreadcrumbs competitionName={competitionTag} />
        <Center>
          <NotFoundImage/>
        </Center>
      </div>
    );
  }

  return (
    <>
      {/*
      <SignupFlow
        isOpen={openNew}
        onClose={() => {
          setFalseNew();
        }}
        event={event}
        eventId={eventId}
      />
      */}
      <PageBreadcrumbs competitionName={competitionTag} />
      <AdminToolbar
        onOpen={() => setDisablePostEditing(false)}
        onClose={() => setDisablePostEditing(true)}
        hidden={!session.hasUserMode(Role.ADMIN)}
      >
        <Button
          leftIcon={<WrenchIcon />}
          onClick={() => {
            setConfigSetup(true);
          }}
        >
          Anmälan
        </Button>
        <Button isDisabled={true} leftIcon={<WrenchIcon />} onClick={() => {}}>
          Resultat
        </Button>
        <Button
          disabled={true}
          isDisabled={true}
          leftIcon={<ImageIcon />}
          onClick={() => {}}
        >
          Sponsorer
        </Button>
        <Button
          leftIcon={<PlusIcon />}
          onClick={() => {
            setOpenPostCreationModal(true);
          }}
        >
          Nytt inlägg
        </Button>
      </AdminToolbar>

      <PostFlow
        category={`competition_${competitionTag}`}
        showLegend={false}
        selectedUserMode={disablePostEditing ? null : session.getUserMode()}
        openPostCreationModal={openPostCreationModal}
        setOpenPostCreationModal={setOpenPostCreationModal}
        onLoadedPosts={(numberOfPostsLoaded) => {
          setShowCompetitionDescription(numberOfPostsLoaded == 0);
        }}
      />
      <Spacer h="50px" />

      <VStack
        spacing={6}
        background={"blue.700"}
        textColor={"white"}
        padding={"10px"}
        paddingTop={"20px"}
        paddingBottom={"20px"}
        borderTopRadius={"20px"}
      >
        {configSignup && (
          <SetupSignupModal
            competition={competition}
            onClose={async () => {
              setConfigSetup(false);
              await delay(1000);
              CompetitionClient.getCompetition(competitionTag).then((c) => {
                if (c.body) {
                  setCompetition(c.body);
                }
              });
            }}
          />
        )}
        <HStack
          alignItems="flex-start"
          w="100%"
          mb={6}
          hidden={!showCompetitionDescription}
        >
          <div>
            <Heading as="h2" size="lg" mb={4}>
              {competition.name}
            </Heading>
            <Text>{competition.description}</Text>
          </div>
          <Spacer />
        </HStack>
        <Show above="md">
          <Stack
            direction={"row"}
            spacing={6}
            justifyContent="space-evenly"
            w="100%"
          >
            <div>
              <Text fontWeight={"bold"}>Startdatum</Text>
              <Text fontWeight="medium">
                {competition.startTime
                  ? format(new Date(competition.startTime), "PPPP")
                  : "-"}
              </Text>
            </div>
            <div>
              <Text fontWeight={"bold"}>Plats</Text>
              <Text fontWeight="medium">
                {competition.place ?? "Ingen plats angiven"}
              </Text>
            </div>
            <div>
              <Text fontWeight={"bold"}>Åldrar</Text>
              <Text fontWeight="medium">
                {competition.minAge && competition.maxAge
                  ? competition.minAge + "-" + competition.maxAge + " år"
                  : null}
                {competition.minAge && !competition.maxAge
                  ? "Upp till " + competition.minAge + " år"
                  : null}
                {!competition.minAge && competition.maxAge
                  ? "Från " + competition.maxAge + " år"
                  : null}
                {!competition.minAge && !competition.maxAge
                  ? "För alla åldrar"
                  : null}
              </Text>
            </div>
          </Stack>
        </Show>
        <Show below="md">
          <Stack
            alignItems={"center"}
            direction={"column"}
            spacing={2}
            justifyContent="space-evenly"
            w="100%"
          >
            <Text fontWeight={"bold"}>Startdatum</Text>
            <Text fontWeight="medium">
              {competition.startTime
                ? format(new Date(competition.startTime), "PPPP")
                : "-"}
            </Text>
            <Text marginTop={4} fontWeight={"bold"}>
              Plats
            </Text>
            <Text fontWeight="medium">
              {competition.place ?? "Ingen plats angiven"}
            </Text>
            <Text marginTop={4} fontWeight={"bold"}>
              Åldrar
            </Text>
            <Text fontWeight="medium">
              {competition.minAge && competition.maxAge
                ? competition.minAge + "-" + competition.maxAge + " år"
                : null}
              {competition.minAge && !competition.maxAge
                ? "Upp till " + competition.minAge + " år"
                : null}
              {!competition.minAge && competition.maxAge
                ? "Från " + competition.maxAge + " år"
                : null}
              {!competition.minAge && !competition.maxAge
                ? "För alla åldrar"
                : null}
            </Text>
          </Stack>
        </Show>
        <Stack
          direction={isSmallScreen ? "row" : "column"}
          width={"100%"}
          alignItems={"center"}
        >
          <Button
            margin={"0px"}
            padding={"30px"}
            width={"80%"}
            colorScheme="green"
            rightIcon={<Edit />}
            as={Link}
            to={"anmalan"}
            key={competition.id}
          >
            Anmälan
          </Button>
          <Button
            margin={"0px"}
            padding={"30px"}
            background="blue.400"
            colorScheme="green"
            width={"80%"}
            to={"startlista"}
            key={competition.id}
            as={Link}
          >
            Startlista
          </Button>
          <Button
            margin={"0px"}
            padding={"30px"}
            width={"80%"}
            background="blue.400"
            colorScheme="blue"
            to={"resultat"}
            hidden={false}
            as={Link}
          >
            Resultat
          </Button>
        </Stack>
        <HStack
          direction={isSmallScreen ? "row" : "column"}
          width={"100%"}
          alignItems={"center"}
          spacing={"200px"}
          display={"flex"}
          visibility={
            session.hasAtLeastRole(Role.OFFICIAL) ? "visible" : "collapse"
          }
        >
            <Tooltip
              isDisabled={session.hasAtLeastRole(Role.OFFICIAL)}
              label={
                "Logga in med den e-mailadressen som du angav vid din anmälan för att kunna göra ändringar."
              }
            >
              <Button
                margin={"0px"}
                padding={"30px"}
                width={"80%"}
                background="blue.400"
                colorScheme="green"
                disabled={!session.hasAtLeastRole(Role.OFFICIAL)}
                isDisabled={!session.hasAtLeastRole(Role.OFFICIAL)}
                as={Link}
                to={"modify"}
                rightIcon={<WrenchIcon />}
              >
                Uppdatera anmälan
              </Button>
            </Tooltip>
          <ResultDownloadPopupView
            competition={competition}
          />
        </HStack>
      </VStack>
      <VStack
        spacing={6}
        //background={"gray.200"}
        background={"blue.900"}
        border={"2px"}
        borderTop={"0px"}
        borderColor={"black"}
        textColor={"white"}
        padding={"10px"}
        paddingTop={"20px"}
        paddingBottom={"20px"}
        borderBottomRadius={"20px"}
      ></VStack>

      <AdminToolbar
        onOpen={() => setDisablePostEditing(false)}
        onClose={() => setDisablePostEditing(true)}
        hidden={!session.hasUserMode(Role.ADMIN)}
      >
        <Button
          disabled={true}
          isDisabled={true}
          leftIcon={<ImageIcon />}
          onClick={() => {}}
        >
          Sponsorer
        </Button>
        <Button
          leftIcon={<PlusIcon />}
          onClick={() => {
            setOpenPostCreationModal2(true);
          }}
        >
          Nytt inlägg
        </Button>
      </AdminToolbar>
      <PostFlow
        category={`competition_part2_${competitionTag}`}
        showLegend={false}
        selectedUserMode={disablePostEditing ? null : session.getUserMode()}
        openPostCreationModal={openPostCreationModal2}
        setOpenPostCreationModal={setOpenPostCreationModal2}
        onLoadedPosts={(numberOfPostsLoaded) => {
          /*Do nothing*/
        }}
      />

      <Box
        p={4}
        shadow="2xl"
        marginTop={"50px"}
        rounded="xl"
        border={"gray.300"}
        borderWidth={"2px"}
        marginBottom={"20px"}
      >
        {
        <Sponsors
          sponsors={sponsors.filter(s => competitionSponsorTypes.includes(s.context as SponsorContext))}
          mainHeading={`Tack för att ni sponsrar ${competition.name}!`}
          hinderHeading="Hinderbanan sponsras av"
        />
        }
      </Box>
    </>
  );
};

function PageBreadcrumbs(props: { competitionName: string }) {
  return (
    <Breadcrumb mb={8}>
      <BreadcrumbItem>
        <BreadcrumbLink as={Link} to="../..">
          Start
        </BreadcrumbLink>
      </BreadcrumbItem>

      <BreadcrumbItem>
        <BreadcrumbLink as={Link} to="..">
          Tävlingar
        </BreadcrumbLink>
      </BreadcrumbItem>

      <BreadcrumbItem isCurrentPage>
        <BreadcrumbLink>{props.competitionName}</BreadcrumbLink>
      </BreadcrumbItem>
    </Breadcrumb>
  );
}
